import type * as React from "react";
import { Box, Typography } from "@mui/material";
import { MembershipInviteRecord } from "apis/v3/authApi";
import { COLORS } from "components/v3/Theme/colors";

type InvitesCardProps = {
  title: string;
  invites: MembershipInviteRecord[];
};

type InviteFieldProps = {
  label: string;
  value: string;
};

const UNIX_SECONDS_MAX = 1e12;

function formatInvitationDate(unixSeconds: number): string | null {
  if (
    typeof unixSeconds !== "number" ||
    !Number.isFinite(unixSeconds) ||
    unixSeconds <= 0 ||
    unixSeconds >= UNIX_SECONDS_MAX
  ) {
    return null;
  }

  const date = new Date(unixSeconds * 1000);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "short"
  }).format(date);
}

const desktopColumnsSx = {
  alignItems: "center",
  display: "grid",
  gap: 2,
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 0.9fr)"
} as const;

const InviteField: React.FC<InviteFieldProps> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="body_sm"
      sx={{ color: COLORS.DARK_GRAY, fontWeight: 600, mb: 0.25 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: COLORS.DARK_GRAY_2, wordBreak: "break-word" }}
    >
      {value}
    </Typography>
  </Box>
);

const InvitesCard: React.FC<InvitesCardProps> = ({ title, invites }) => {
  return (
    <Box
      sx={{
        backgroundColor: COLORS.BACKGROUND_BLUE,
        borderRadius: "12px",
        boxSizing: "border-box",
        maxWidth: "100%",
        minWidth: 0,
        padding: 2.5,
        width: "100%"
      }}
    >
      <Typography
        sx={{
          color: COLORS.BLACK,
          fontSize: "1.125rem",
          fontWeight: 700,
          mb: 2
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: { sm: "none", xs: "flex" }, flexDirection: "column" }}>
        {invites.map((invite, index) => {
          const invitationDate = formatInvitationDate(invite.invitationDate);

          return (
            <Box
              key={invite.inviteId}
              sx={{
                borderBottom:
                  index < invites.length - 1
                    ? `1px solid ${COLORS.PRIMARY_LIGHTER}`
                    : "none",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                py: 1.5
              }}
            >
              <InviteField
                label="Name"
                value={`${invite.firstName} ${invite.lastName}`.trim()}
              />
              <InviteField label="Email" value={invite.email} />
              {invitationDate && (
                <InviteField label="Date" value={invitationDate} />
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: { sm: "block", xs: "none" } }}>
        <Box
          sx={{
            ...desktopColumnsSx,
            borderBottom: `1px solid ${COLORS.PRIMARY_LIGHTER}`,
            pb: 1
          }}
        >
          <Typography
            variant="body_sm"
            sx={{ color: COLORS.DARK_GRAY, fontWeight: 600 }}
          >
            Name
          </Typography>
          <Typography
            variant="body_sm"
            sx={{ color: COLORS.DARK_GRAY, fontWeight: 600 }}
          >
            Email
          </Typography>
          <Typography
            variant="body_sm"
            sx={{ color: COLORS.DARK_GRAY, fontWeight: 600 }}
          >
            Date
          </Typography>
        </Box>

        {invites.map((invite, index) => (
          <Box
            key={invite.inviteId}
            sx={{
              ...desktopColumnsSx,
              borderBottom:
                index < invites.length - 1
                  ? `1px solid ${COLORS.PRIMARY_LIGHTER}`
                  : "none",
              py: 1.5
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: COLORS.DARK_GRAY_2, wordBreak: "break-word" }}
            >
              {`${invite.firstName} ${invite.lastName}`.trim()}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: COLORS.DARK_GRAY_2, wordBreak: "break-word" }}
            >
              {invite.email}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.DARK_GRAY_2 }}>
              {formatInvitationDate(invite.invitationDate) ?? ""}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default InvitesCard;
